import { FC, Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Listbox, Switch } from '@headlessui/react';
import classNames from 'classnames';
import { DEFAULT_FREE_CLIENT_COUNT } from '../../lib/constants';
import { SUPPORT_MAIL_TO_URL } from '../../lib/url';
import ToastAlert from '../ToastAlert';
import toast from 'react-hot-toast';
import {
  SubscriptionTierV2,
  useProviderIsOnLegacyV2PricingQuery,
  useStripeCheckoutPortalLazyQuery,
  useStripeCustomerPortalLazyQuery,
} from '../../../generated/graphql';
import { ApolloError } from '@apollo/client';
import { useAuth } from '../../../contexts/AuthContext';
import {
  ProviderAnalyticsEvent,
  AnalyticsPage,
  trackProviderEvent,
  CommonAnalyticsEvent,
} from '../../../lib/analytics';
import { ChevronDownIcon } from '@heroicons/react/outline';
import { BillingState } from '../Billing/BillingSection';
import { getTimeStamp } from '../../lib/time';
import Button from '../Button';
import { HomecomingError, matchHomecomingError } from '../../../lib/errors';
import FeatureCell from './FeatureCell';
import Config from '../../../lib/config';
import MobilePricingCard from './MobilePricingCard';
import DevUpgradeComponent from './DevUpgradeComponent';

export enum BillingPeriodOption {
  Annual = 'Annual',
  Monthly = 'Monthly',
}

export interface BillingPeriodInfo {
  period: BillingPeriodOption;
  /** Effective monthly price per seat for the selected billing period */
  perSeatMonthlyPrice: number;
  /** Yearly total per seat when billed annually */
  perSeatAnnualPrice?: number;
  label?: string;
}

/** $59/seat/mo; annual is $599/seat/yr (~$50/seat/mo, ~15% off) */
export const BILLING_PERIODS: Record<BillingPeriodOption, BillingPeriodInfo> = {
  [BillingPeriodOption.Annual]: {
    period: BillingPeriodOption.Annual,
    perSeatMonthlyPrice: 599 / 12,
    perSeatAnnualPrice: 599,
    label: 'Save 15%',
  },
  [BillingPeriodOption.Monthly]: {
    period: BillingPeriodOption.Monthly,
    perSeatMonthlyPrice: 59,
  },
};

const MONTHLY_PER_SEAT_PRICE =
  BILLING_PERIODS[BillingPeriodOption.Monthly].perSeatMonthlyPrice;

/** Historical Team base used for Stripe extraSeatCount = seats - 3 (Team 2 → -1). */
export const TEAM_BASE_SEAT_COUNT = 3;

// 1–5 seats (net-new self-serve Premium column)
export const PREMIUM_SEAT_COUNT_OPTIONS = [1, 2, 3, 4, 5];

/** @deprecated Use PREMIUM_SEAT_COUNT_OPTIONS */
export const TEAM_SEAT_COUNT_OPTIONS = PREMIUM_SEAT_COUNT_OPTIONS;

export type FeatureRow = {
  title: string;
  freeValue: string | boolean | null;
  premiumValue: string | boolean | null;
  enterpriseValue: string | boolean;
  freeValueMobile?: string;
  premiumValueMobile?: string;
  enterpriseValueMobile?: string;
};

export const formatUsd = (amount: number): string => `$${Math.round(amount)}`;

const CurrentPlanButtonLabel: FC<{ showLegacyRateNote?: boolean }> = ({
  showLegacyRateNote = false,
}) => {
  return (
    <div className="flex w-full flex-col items-center gap-y-2">
      <Button
        title="Current plan"
        className="w-full !cursor-auto !bg-green-25 !text-green-125"
        disabled={true}
      />
      {showLegacyRateNote && (
        <div className="text-center text-caption font-medium text-neutral-110">
          You're on a legacy rate
        </div>
      )}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const hidePricingTable = false;

const ContactUsForPricing: FC = () => (
  <div className="mt-16 flex max-w-xl flex-col items-center justify-center gap-y-6 rounded-xl bg-white p-10 shadow-md">
    <div className="text-center font-serif text-title text-neutral-150">
      Learn more about our plans
    </div>
    <div className="text-center text-body text-neutral-125">
      If you're interested in Homecoming, please get in touch with us directly
      and we'll be happy to discuss options.
    </div>
    <Button
      title="Email us"
      className="text-lg w-full bg-green-100 text-white hover:bg-green-125"
      onClick={() => {
        window.location.href = SUPPORT_MAIL_TO_URL;
      }}
    />
  </div>
);

const PricingTable: FC<{
  isPricingEmbed: boolean;
  annualSelected: boolean;
  selectedBillingPeriod: BillingPeriodInfo;
  showManageBilling: boolean;
  showCurrentPlanLabel: boolean;
  showLegacyRateNote: boolean;
  goToCorrectStripePortal: () => void;
  stripeCheckoutPortalLoading: boolean;
  stripeCustomerPortalLoading: boolean;
  premiumSeatCount: number;
  setPremiumSeatCount: (n: number) => void;
  featureRows: FeatureRow[];
  premiumEffectiveMonthlyPrice: number;
  premiumAnnualPrice: number;
  premiumSavings: number;
  isTrialing: boolean;
  subscriptionStatus: string;
  switchSelectedBillingPeriod: (
    previousBillingPeriod: BillingPeriodOption,
  ) => void;
}> = ({
  isPricingEmbed,
  annualSelected,
  selectedBillingPeriod,
  showManageBilling,
  showCurrentPlanLabel,
  showLegacyRateNote,
  goToCorrectStripePortal,
  stripeCheckoutPortalLoading,
  stripeCustomerPortalLoading,
  premiumSeatCount,
  setPremiumSeatCount,
  featureRows,
  premiumEffectiveMonthlyPrice,
  premiumAnnualPrice,
  premiumSavings,
  isTrialing,
  subscriptionStatus,
  switchSelectedBillingPeriod,
}) => (
  <>
    <div className="mb-8 flex flex-row items-center gap-x-3">
      <Switch
        checked={annualSelected}
        onChange={() =>
          switchSelectedBillingPeriod(selectedBillingPeriod.period)
        }
        className={classNames(
          'relative inline-flex h-[48px] w-[209px] flex-shrink-0 cursor-pointer rounded-full',
          'focus:outline-none focus:ring-2 focus:ring-blue-50 focus:ring-offset-2',
          isPricingEmbed ? 'bg-white' : 'bg-neutral-50',
        )}
      >
        <div
          className={classNames(
            annualSelected ? 'translate-x-full' : 'translate-x-0',
            'pointer-events-none absolute top-1 left-1 inline-block h-[40px] w-[101px] transform rounded-full bg-blue-25 ring-0 transition duration-200 ease-in-out',
          )}
        ></div>
        <div
          className={classNames(
            'absolute top-1 left-0.5 flex h-[40px] w-[101px] items-center justify-center text-body font-medium text-neutral-150',
          )}
          aria-hidden="true"
        >
          Monthly
        </div>
        <div
          className={classNames(
            'absolute top-1 right-0.5 flex h-[40px] w-[101px] items-center justify-center text-body font-medium text-neutral-150',
          )}
          aria-hidden="true"
        >
          Annual
        </div>
      </Switch>
      <div
        className={classNames(
          'w-[80px] rounded bg-green-100 px-2 py-1 text-action font-bold uppercase text-white',
          annualSelected ? 'opacity-100' : 'opacity-50',
        )}
      >
        Save 15%
      </div>
    </div>
    <div
      className={classNames(
        isPricingEmbed
          ? 'hidden rounded-xl bg-white p-5 opacity-[98%] lg:grid'
          : 'grid',
        'mt-8 min-w-[72rem] max-w-[72rem] grid-cols-4 divide-x divide-neutral-50 rounded-md border border-neutral-50',
      )}
    >
      {/* Title row */}
      <div className="p-6 font-serif text-subtitle font-light text-neutral-150">
        Plans
      </div>
      <div className="relative p-6 font-serif text-subtitle font-light text-neutral-150">
        Free
      </div>
      <div className="relative p-6 font-serif text-subtitle font-light text-neutral-150">
        Premium
        {isTrialing && (
          <div className="absolute -top-8 left-0 flex h-8 w-full items-center justify-center rounded-t-md bg-orange-50 text-center font-sans text-small-caption font-medium text-orange-125">
            {subscriptionStatus}
          </div>
        )}
      </div>
      <div className="p-6 font-serif text-subtitle font-light text-neutral-150">
        Enterprise
      </div>

      {/* Pricing row */}
      <div className="p-6" />
      <div className="flex flex-col p-6">
        <div className="flex h-[60px] items-center gap-x-2">
          <div className="text-[2.872rem] font-medium leading-tight text-neutral-150">
            $0
          </div>
          <div className="max-w-[141px] text-caption font-medium text-neutral-110">
            Free forever
          </div>
        </div>
      </div>
      <div className="flex flex-col p-6">
        <div className="flex h-[60px] items-center gap-x-2">
          <div className="text-[2.872rem] font-medium leading-tight text-neutral-150">
            {formatUsd(premiumEffectiveMonthlyPrice)}
          </div>
          <div className="max-w-[141px] text-caption font-medium text-neutral-110">
            USD/month <br />
            billed {annualSelected ? 'annually' : 'monthly'}
          </div>
        </div>
        <div className="mt-1 flex h-6 items-center gap-x-4">
          {annualSelected && (
            <>
              <div className="rounded bg-green-100 px-2 py-1 text-action font-bold uppercase text-white">
                Save {formatUsd(premiumSavings)}
              </div>
              <div className="text-caption font-medium text-neutral-110">
                {formatUsd(premiumAnnualPrice)} annually
              </div>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="text-title-medium font-medium leading-tight text-neutral-150">
          Let's talk
        </div>
      </div>

      {/* CTA row */}
      <div className="p-6" />
      <div className="p-6" />
      <div className="p-6">
        {showCurrentPlanLabel ? (
          <CurrentPlanButtonLabel showLegacyRateNote={showLegacyRateNote} />
        ) : (
          <Button
            title={
              isPricingEmbed
                ? 'Start 30-day free trial'
                : showManageBilling
                ? 'Manage billing'
                : 'Subscribe'
            }
            className="w-full bg-blue-125 hover:bg-blue-100"
            onClick={() => {
              if (isPricingEmbed) {
                window.open(
                  `${Config.REACT_APP_WEB_APP_URL}/sign-up`,
                  '_blank',
                  'noopener noreferrer',
                );
              } else {
                goToCorrectStripePortal();
              }
            }}
            disabled={
              stripeCheckoutPortalLoading || stripeCustomerPortalLoading
            }
          />
        )}
      </div>
      <div className="p-6">
        <Button
          title="Contact us"
          className="w-full bg-blue-125 hover:bg-blue-100"
          onClick={() => {
            trackProviderEvent(CommonAnalyticsEvent.ButtonClicked, {
              originPage: AnalyticsPage.Subscribe,
              buttonName: 'Enterprise - Contact us',
            });
            window.location.href = SUPPORT_MAIL_TO_URL;
          }}
        />
      </div>

      {/* Seat selection row */}
      <div className="py-6 px-10 text-body text-green-150">
        Team member seats
      </div>
      <div className="py-6 px-8">
        <div className="flex justify-center rounded-full bg-neutral-25 py-2 px-11 text-center text-body font-medium text-neutral-150">
          1 seat
        </div>
      </div>
      <div className="relative py-6 px-8">
        <Listbox value={premiumSeatCount} onChange={setPremiumSeatCount}>
          <Listbox.Button className="flex w-full justify-center rounded-full bg-neutral-25 py-2 px-11 text-center text-body font-medium text-neutral-150">
            {premiumSeatCount} {premiumSeatCount === 1 ? 'seat' : 'seats'}{' '}
            <ChevronDownIcon className="ml-2 h-5 w-5" />
          </Listbox.Button>
          <Listbox.Options className="absolute z-50 w-[15rem] divide-y divide-neutral-75 rounded-md border border-neutral-75 bg-white shadow-lg focus:outline-none">
            {PREMIUM_SEAT_COUNT_OPTIONS.map((seatCount, i) => (
              <Listbox.Option
                key={`seatCountOption-${seatCount}`}
                value={seatCount}
                className={({ active }) =>
                  classNames(
                    'cursor-pointer px-5 py-3 text-caption text-neutral-125',
                    active && 'bg-neutral-25',
                    {
                      'rounded-t-md': active && i === 0,
                      'rounded-b-md':
                        active && i === PREMIUM_SEAT_COUNT_OPTIONS.length - 1,
                    },
                  )
                }
              >
                {seatCount} {seatCount === 1 ? 'seat' : 'seats'}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Listbox>
      </div>
      <div className="py-6 px-8">
        <div className="flex justify-center rounded-full bg-neutral-25 py-2 px-11 text-center text-body font-medium text-neutral-150">
          6+ seats
        </div>
      </div>

      {/* Feature rows */}
      {featureRows.map((row, i) => (
        <Fragment key={`featureRow_${i}`}>
          <FeatureCell value={row.title} className="text-left" />
          <FeatureCell
            value={row.freeValue}
            className="justify-center text-center"
          />
          <FeatureCell
            value={row.premiumValue}
            className="justify-center text-center"
          />
          <FeatureCell
            value={row.enterpriseValue}
            className="justify-center text-center"
          />
        </Fragment>
      ))}
    </div>
  </>
);

const Subscription: FC<{
  isPricingEmbed?: boolean;
}> = ({ isPricingEmbed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const stripeCheckoutPortalWebRedirectPath = (
    location.state as { stripeCheckoutPortalWebRedirectPath?: string }
  )?.stripeCheckoutPortalWebRedirectPath;
  const isOnboarding = Boolean(
    (location.state as { isOnboarding?: string })?.isOnboarding ??
      searchParams.get('isOnboarding') ??
      false,
  );

  const { authedProviderUser } = useAuth();

  const isSubscribed = authedProviderUser?.isProviderSubscribed ?? false;
  const isTrialing = authedProviderUser?.isTrialing ?? false;
  const trialEndsAt =
    isTrialing && authedProviderUser ? authedProviderUser.trialEndsAt : null;
  const subscriptionEndsAt =
    authedProviderUser?.provider.subscriptionEndsAt ?? null;
  const currentSubscriptionTier =
    authedProviderUser?.provider.subscriptionTierV2;

  const billingState =
    isSubscribed && !subscriptionEndsAt
      ? BillingState.Subscribed
      : isSubscribed && subscriptionEndsAt
      ? BillingState.SubscriptionEnding
      : isTrialing
      ? BillingState.Trialing
      : BillingState.Unsubscribed;

  const subscriptionStatus =
    billingState === BillingState.Subscribed
      ? 'Subscribed'
      : billingState === BillingState.SubscriptionEnding
      ? `Subscribed until ${getTimeStamp(
          subscriptionEndsAt,
          false,
          false,
          true,
        )}`
      : billingState === BillingState.Trialing
      ? `Trialing until ${getTimeStamp(trialEndsAt, false, false, true)}`
      : 'Not subscribed';

  const [getStripeCheckoutPortal, { loading: stripeCheckoutPortalLoading }] =
    useStripeCheckoutPortalLazyQuery();

  const [selectedBillingPeriodOption, setSelectedBillingPeriodOption] =
    useState<BillingPeriodOption>(BillingPeriodOption.Annual);

  const currentStaffSeats =
    authedProviderUser?.provider?.seatUsage?.staffSeats ?? 1;

  const isOnPaidPlan =
    currentSubscriptionTier === SubscriptionTierV2.Premium ||
    currentSubscriptionTier === SubscriptionTierV2.Team;

  const [premiumSeatCount, setPremiumSeatCount] = useState<number>(1);
  const [hasInitializedSeats, setHasInitializedSeats] = useState(false);

  // Seed picker from current seats when subscribed (1–5); otherwise default to 1.
  useEffect(() => {
    if (hasInitializedSeats || !authedProviderUser) {
      return;
    }
    if (isOnPaidPlan && currentStaffSeats >= 1 && currentStaffSeats <= 5) {
      setPremiumSeatCount(currentStaffSeats);
    }
    setHasInitializedSeats(true);
  }, [
    authedProviderUser,
    isOnPaidPlan,
    currentStaffSeats,
    hasInitializedSeats,
  ]);

  const { data: legacyPricingData } = useProviderIsOnLegacyV2PricingQuery({
    skip: isPricingEmbed || !isOnPaidPlan,
  });

  const isOnLegacyRate =
    legacyPricingData?.meProvider?.provider?.isOnLegacyV2Pricing ?? false;

  const hasSeatCountChanged =
    isOnPaidPlan && premiumSeatCount !== currentStaffSeats;

  // Legacy 6+ seat plans can't match the 1–5 picker — never show Current plan.
  const showCurrentPlanLabel =
    isOnPaidPlan &&
    currentStaffSeats >= 1 &&
    currentStaffSeats <= 5 &&
    !hasSeatCountChanged;

  const showManageBilling = isOnPaidPlan && !showCurrentPlanLabel;
  const showLegacyRateNote = showCurrentPlanLabel && isOnLegacyRate;

  const selectedBillingPeriod = BILLING_PERIODS[selectedBillingPeriodOption];
  const annualSelected =
    selectedBillingPeriodOption === BillingPeriodOption.Annual;

  const switchSelectedBillingPeriod = (
    previousBillingPeriod: BillingPeriodOption,
  ) => {
    if (previousBillingPeriod === BillingPeriodOption.Annual) {
      setSelectedBillingPeriodOption(BillingPeriodOption.Monthly);
    } else {
      setSelectedBillingPeriodOption(BillingPeriodOption.Annual);
    }
  };

  const goToStripeCheckoutPortal = async () => {
    const tierSelection =
      premiumSeatCount === 1
        ? SubscriptionTierV2.Premium
        : SubscriptionTierV2.Team;
    const extraSeatCount =
      premiumSeatCount === 1
        ? undefined
        : premiumSeatCount - TEAM_BASE_SEAT_COUNT;

    trackProviderEvent(CommonAnalyticsEvent.ButtonClicked, {
      originPage: AnalyticsPage.Subscribe,
      buttonName: 'Subscribe',
      billingPeriodSelected: annualSelected
        ? BillingPeriodOption.Annual
        : BillingPeriodOption.Monthly,
      tierSelected: tierSelection,
      extraSeatCount,
      seatCount: premiumSeatCount,
    });

    try {
      const { data, error } = await getStripeCheckoutPortal({
        variables: {
          webRedirectPath: stripeCheckoutPortalWebRedirectPath ?? 'subscribe',
          choseAnnual: annualSelected,
          isOnboarding,
          tierSelection,
          extraSeatCount,
        },
      });

      if (error) {
        throw error;
      }

      if (data?.stripeCheckoutPortal.url) {
        window.location.href = data.stripeCheckoutPortal.url;
      }
    } catch (err) {
      console.error('Error getting Stripe Checkout Portal:', err);

      if (
        matchHomecomingError(
          err as ApolloError,
          HomecomingError.ResourceForbidden,
        )
      ) {
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            message="You're already subscribed."
            level="error"
          />
        ));
        trackProviderEvent(ProviderAnalyticsEvent.ProviderCheckoutFailed, {
          reason: 'Already subscribed',
        });
        navigate('/');
        return;
      }

      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Something went wrong. Please contact us at support@homecoming.health."
          level="error"
        />
      ));
      trackProviderEvent(ProviderAnalyticsEvent.ProviderCheckoutFailed, {
        reason: 'Server error',
      });
    }
  };

  const [getStripeCustomerPortal, { loading: stripeCustomerPortalLoading }] =
    useStripeCustomerPortalLazyQuery();

  const goToStripeCustomerPortal = async () => {
    trackProviderEvent(CommonAnalyticsEvent.ButtonClicked, {
      originPage: AnalyticsPage.Subscribe,
      buttonName: 'Upgrade / Downgrade',
      billingPeriodSelected: annualSelected
        ? BillingPeriodOption.Annual
        : BillingPeriodOption.Monthly,
    });

    try {
      const { data } = await getStripeCustomerPortal();

      if (data?.stripeCustomerPortal.url) {
        window.location.href = data.stripeCustomerPortal.url;
      }
    } catch (err) {
      console.error('Error getting Stripe Customer Portal:', err);
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Something went wrong. Please contact us at support@homecoming.health."
          level="error"
        />
      ));
    }
  };

  const goToCorrectStripePortal = async () => {
    if (isOnPaidPlan) {
      goToStripeCustomerPortal();
    } else {
      goToStripeCheckoutPortal();
    }
  };

  const featureRows: FeatureRow[] = [
    {
      title: 'Clients',
      freeValue: `${DEFAULT_FREE_CLIENT_COUNT} clients`,
      freeValueMobile: `Limit of ${DEFAULT_FREE_CLIENT_COUNT} clients`,
      premiumValue: 'Unlimited',
      enterpriseValue: 'Unlimited',
    },
    {
      title: 'Practitioner dashboard and mobile app',
      freeValue: true,
      premiumValue: true,
      enterpriseValue: true,
    },
    {
      title: 'Client portal and mobile app',
      freeValue: true,
      premiumValue: true,
      enterpriseValue: true,
    },
    {
      title: 'Webpage for scheduling and intake',
      freeValue: true,
      premiumValue: true,
      enterpriseValue: true,
    },
    {
      title: 'Forms and digital signatures',
      freeValue: true,
      premiumValue: true,
      enterpriseValue: true,
    },
    {
      title: 'Automations',
      freeValue: true,
      premiumValue: true,
      enterpriseValue: true,
    },
    {
      title: 'Team access roles and permissions',
      freeValue: null,
      premiumValue: premiumSeatCount >= 2 ? true : null,
      enterpriseValue: true,
    },
    {
      title: 'Individual assignment of clients to team members',
      freeValue: null,
      premiumValue: premiumSeatCount >= 2 ? true : null,
      enterpriseValue: true,
    },
    {
      title: 'Collaborate with other providers on Homecoming',
      freeValue: 'Limited',
      premiumValue: true,
      enterpriseValue: true,
    },
    {
      title: 'Launch & migration support',
      freeValue: null,
      premiumValue: true,
      enterpriseValue: true,
    },
    {
      title: 'Support',
      freeValue: 'Chat & email within 2 business days',
      freeValueMobile: 'Support through chat & email within 2 business days',
      premiumValue: 'Chat, email, Zoom within 1 business day',
      premiumValueMobile:
        'Support through chat, email, Zoom within 1 business day',
      enterpriseValue: 'Chat, email, Zoom within 1 business day',
      enterpriseValueMobile:
        'Support through chat, email, Zoom within 1 business day',
    },
    {
      title: 'Bulk seat pricing',
      freeValue: null,
      premiumValue: null,
      enterpriseValue: true,
    },
    {
      title: 'Dedicated account manager',
      freeValue: null,
      premiumValue: null,
      enterpriseValue: true,
    },
    {
      title: 'Custom implementation',
      freeValue: null,
      premiumValue: null,
      enterpriseValue: true,
    },
  ];

  const premiumEffectiveMonthlyPrice = Number(
    (selectedBillingPeriod.perSeatMonthlyPrice * premiumSeatCount).toFixed(2),
  );
  const premiumAnnualPrice = Number(
    (
      (selectedBillingPeriod.perSeatAnnualPrice ?? 0) * premiumSeatCount
    ).toFixed(2),
  );
  const premiumSavings = Number(
    (
      MONTHLY_PER_SEAT_PRICE * 12 * premiumSeatCount -
      premiumAnnualPrice
    ).toFixed(2),
  );

  return (
    <div
      className={classNames(
        'flex flex-col items-center px-10 sm:justify-center',
        !isPricingEmbed ? 'py-20' : 'pb-10',
      )}
    >
      {hidePricingTable ? (
        <>
          <ContactUsForPricing />
          {Config.IS_DEV && <DevUpgradeComponent />}
        </>
      ) : (
        <PricingTable
          isPricingEmbed={isPricingEmbed}
          annualSelected={annualSelected}
          selectedBillingPeriod={selectedBillingPeriod}
          showManageBilling={showManageBilling}
          showCurrentPlanLabel={showCurrentPlanLabel}
          showLegacyRateNote={showLegacyRateNote}
          goToCorrectStripePortal={goToCorrectStripePortal}
          stripeCheckoutPortalLoading={stripeCheckoutPortalLoading}
          stripeCustomerPortalLoading={stripeCustomerPortalLoading}
          premiumSeatCount={premiumSeatCount}
          setPremiumSeatCount={setPremiumSeatCount}
          featureRows={featureRows}
          premiumEffectiveMonthlyPrice={premiumEffectiveMonthlyPrice}
          premiumAnnualPrice={premiumAnnualPrice}
          premiumSavings={premiumSavings}
          isTrialing={isTrialing}
          subscriptionStatus={subscriptionStatus}
          switchSelectedBillingPeriod={switchSelectedBillingPeriod}
        />
      )}
      {isPricingEmbed && (
        <div className="flex flex-col items-center justify-start gap-y-6 lg:hidden">
          <MobilePricingCard
            annualSelected={annualSelected}
            featureRows={featureRows}
            planValue="freeValue"
          />
          <MobilePricingCard
            annualSelected={annualSelected}
            featureRows={featureRows}
            planValue="premiumValue"
            premiumEffectiveMonthlyPrice={premiumEffectiveMonthlyPrice}
            premiumSavings={premiumSavings}
            premiumAnnualPrice={premiumAnnualPrice}
            premiumSeatCount={premiumSeatCount}
            setPremiumSeatCount={setPremiumSeatCount}
          />
          <MobilePricingCard
            annualSelected={annualSelected}
            featureRows={featureRows}
            planValue="enterpriseValue"
          />
        </div>
      )}
    </div>
  );
};

export default Subscription;
